export interface CorrelationContext {
  conversationId: string;
  connectionEpoch: number;
  reconnectAttemptId: string;
  turnId: string;
}

export interface TimelineEvent {
  event: string;
  timestamp: number;
  perfTime: number;
  correlation: CorrelationContext;
  details?: Record<string, unknown>;
}

const TIMELINE_LIMIT = 200;
const GLOBAL_KEY = 'fachnew.conversation_id';

const createId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const getStoredConversationId = (): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage.getItem(GLOBAL_KEY);
  } catch {
    return null;
  }
};

const setStoredConversationId = (value: string): void => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(GLOBAL_KEY, value);
  } catch {
    // Ignore storage quota/private mode failures.
  }
};

export const getOrCreateConversationId = (): string => {
  const existing = getStoredConversationId();
  if (existing) return existing;
  const created = createId();
  setStoredConversationId(created);
  return created;
};

export const setConversationId = (conversationId: string): void => {
  setStoredConversationId(conversationId);
};

export class SentryLogger {
  private correlation: CorrelationContext;
  private timeline: TimelineEvent[] = [];

  constructor(conversationId?: string) {
    this.correlation = {
      conversationId: conversationId ?? getOrCreateConversationId(),
      connectionEpoch: 0,
      reconnectAttemptId: createId(),
      turnId: createId(),
    };
    setConversationId(this.correlation.conversationId);
  }

  getCorrelationIds(): CorrelationContext {
    return { ...this.correlation };
  }

  getConversationId(): string {
    return this.correlation.conversationId;
  }

  setConnectionEpoch(epoch: number): void {
    this.correlation.connectionEpoch = epoch;
  }

  nextReconnectAttemptId(): string {
    this.correlation.reconnectAttemptId = createId();
    return this.correlation.reconnectAttemptId;
  }

  nextTurnId(): string {
    this.correlation.turnId = createId();
    return this.correlation.turnId;
  }

  logEvent(event: string, details?: Record<string, unknown>): void {
    const timelineEvent: TimelineEvent = {
      event,
      timestamp: Date.now(),
      perfTime: typeof performance !== 'undefined' ? performance.now() : Date.now(),
      correlation: this.getCorrelationIds(),
      details,
    };
    this.timeline.push(timelineEvent);
    if (this.timeline.length > TIMELINE_LIMIT) {
      this.timeline.splice(0, this.timeline.length - TIMELINE_LIMIT);
    }
  }

  getTimeline(): TimelineEvent[] {
    return [...this.timeline];
  }
}
